import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, RefreshCw, Search, Loader2, Trash2, User as UserIcon, Store, Mail } from "lucide-react";

const REASON_LABELS = {
  invalid_period: { label: "Bad period value", tone: "amber" },
  invalid_tier: { label: "Bad tier value", tone: "amber" },
  business_not_found: { label: "Business missing", tone: "red" },
  not_owner: { label: "Wrong owner", tone: "red" },
  already_active_same_or_lower_tier: { label: "Already subscribed", tone: "blue" },
};

const STAGE_LABELS = {
  checkout_start: "Checkout Start",
  checkout_upgrade_cancel_old: "Upgrade Cancel Old",
  checkout_stripe_session_create: "Stripe Session Create",
};

const toneClasses = {
  red: "bg-red-100 text-red-700 border-red-200",
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  blue: "bg-blue-100 text-blue-700 border-blue-200",
  gray: "bg-gray-100 text-gray-700 border-gray-200",
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

/**
 * AdminSubscriptionFailuresCard
 * Surfaces the latest checkout failures so we know exactly why a specific
 * business couldn't upgrade — bad period, wrong owner, Stripe error, etc.
 * Every failure is captured server-side in `subscription_failures` with
 * the requesting user, target business, requested tier, and Stripe error
 * code when applicable.
 */
export default function AdminSubscriptionFailuresCard() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      params.set("limit", "100");
      const r = await axios.get(`${API}/admin/subscription-failures?${params}`, {
      });
      setRows(r.data.failures || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load failures");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const clearOld = async () => {
    if (!window.confirm("Delete failure logs older than 30 days?")) return;
    try {
      const r = await axios.delete(
        `${API}/admin/subscription-failures?older_than_days=30`
      );
      toast.success(`Cleared ${r.data.deleted} old logs`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clear");
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 space-y-4" data-testid="admin-subscription-failures-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-xl text-tbc-navy flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" /> Subscription Upgrade Failures
          </h2>
          <p className="text-sm text-gray-500 font-body">
            Every rejected or failed checkout attempt · {total} total logged
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search email, business, reason…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchAll()}
              className="pl-9 w-64"
              data-testid="admin-failures-search"
            />
          </div>
          <Button variant="outline" onClick={fetchAll} data-testid="admin-failures-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
          <Button variant="outline" onClick={clearOld} className="border-red-300 text-red-600 hover:bg-red-50">
            <Trash2 className="w-4 h-4 mr-1.5" /> Clear 30d+
          </Button>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="py-16 flex items-center justify-center text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading failures…
        </div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-gray-500 font-body">
          🎉 No subscription failures logged. Every upgrade attempt succeeded — or nobody&apos;s tried yet.
        </div>
      ) : (
        <div className="divide-y divide-gray-100 -mx-6">
          {rows.map((f, i) => {
            const reasonKey = (f.reason || "").split(":")[0].trim();
            const meta = REASON_LABELS[reasonKey] || { label: f.reason, tone: "gray" };
            const bizNow = f.business_now;
            const userNow = f.user_now;
            return (
              <div
                key={f.failure_id || f._id || `${f.user_id || "u"}-${f.created_at || i}`}
                className="px-6 py-4 hover:bg-gray-50 transition-colors"
                data-testid={`admin-failure-row-${i}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`${toneClasses[meta.tone]} border font-medium`}>
                        {meta.label}
                      </Badge>
                      <Badge variant="outline" className="text-xs text-gray-600">
                        {STAGE_LABELS[f.stage] || f.stage}
                      </Badge>
                      <Badge variant="outline" className="text-xs text-gray-600">
                        HTTP {f.status_code}
                      </Badge>
                      {f.stripe_error_code && (
                        <Badge className="bg-red-100 text-red-700 border-red-200 border text-xs">
                          Stripe: {f.stripe_error_code}
                        </Badge>
                      )}
                      <span className="text-xs text-gray-400 ml-auto">
                        {formatDate(f.created_at)}
                      </span>
                    </div>

                    <div className="mt-2 grid sm:grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2">
                        <UserIcon className="w-3.5 h-3.5 text-gray-400" />
                        <span className="font-medium text-tbc-navy">
                          {userNow?.name || f.user_email || "unknown user"}
                        </span>
                        {f.user_email && (
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            <a href={`mailto:${f.user_email}`} className="hover:underline">
                              {f.user_email}
                            </a>
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Store className="w-3.5 h-3.5 text-gray-400" />
                        {bizNow ? (
                          <a
                            href={`/business/${bizNow.business_id}`}
                            target="_blank" rel="noreferrer"
                            className="text-tbc-navy hover:text-tbc-gold font-medium truncate"
                          >
                            {bizNow.name}
                          </a>
                        ) : (
                          <span className="text-gray-500 font-mono text-xs">
                            {f.business_id || "no business"}
                          </span>
                        )}
                        {bizNow && (
                          <Badge variant="outline" className="text-xs capitalize">
                            {bizNow.subscription_tier || "free"} / {bizNow.subscription_status || "—"}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="mt-2 text-xs text-gray-500 font-body">
                      Wanted: <strong className="text-tbc-navy capitalize">{f.requested_tier || "?"}</strong>{" "}
                      / <strong className="text-tbc-navy capitalize">{f.requested_period || "?"}</strong>
                      {f.current_tier && (
                        <>
                          {" · Was on: "}
                          <strong className="text-tbc-navy capitalize">{f.current_tier}</strong>
                          {" ("}<em>{f.current_status || "—"}</em>{")"}
                        </>
                      )}
                    </div>

                    {f.reason && f.reason.includes(":") && (
                      <div className="mt-1.5 text-xs text-gray-700 font-mono bg-gray-50 p-2 rounded border border-gray-100 break-words">
                        {f.reason}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
